<template>
	<div
		:id="boxData.boxID ? boxData.boxID : null"
		:class="className"
		:style="
			boxData.graphic && {
				backgroundImage: 'url(' + boxData.graphic + ')',
				backgroundSize: bgSize,
				backgroundPosition: bgPosition
			}
		"
	>
		<div
			v-if="bgColorOverlay"
			class="background-overlay"
			:style="bgColorOverlay"
		/>
		<div class="content-container">
			<h3 class="h3" v-html="boxData.title" />
			<p
				v-for="(text, index) in content"
				:key="'contentbox' + index"
				class="content-box__text"
				:style="contentStyles"
				v-html="text"
			/>
			<a
				:href="boxData.ctaUrl"
				class="button button--tertiary"
				:class="[fgColor === 'white' ? '--inverted' : '--black']"
				v-if="boxData.ctaUrl"
				:title="
					boxData.ctaDescription ? boxData.ctaDescription : 'Opens a new window'
				"
				@click="onButtonClicked"
			>
				<span
					class="icon icon-lock inline-icon"
					role="img"
					aria-label="Must log in to view: "
					v-if="boxData.ctaLocked"
				/>
				<span v-html="boxData.ctaText" />
				<span class="icon icon-right" aria-hidden="true" />
			</a>

			<button
				class="button button--tertiary"
				:class="[fgColor === 'white' ? '--inverted' : '--black']"
				@click="onButtonClicked"
				v-else
			>
				<span
					class="icon icon-lock inline-icon"
					role="img"
					aria-label="Must log in to view: "
					v-if="boxData.ctaLocked"
				/>
				<span v-html="boxData.ctaText" />
				<span class="icon icon-right" aria-hidden="true" />
			</button>
		</div>
		<v-breakpoint v-model="model" />
	</div>
</template>

<script>
import { VBreakpoint, Model } from "vue-breakpoint-component";
import { ButtonEventsMixin } from "./mixins";

export default {
	name: "ContentBox",
	mixins: [ButtonEventsMixin],
	/**
	 * @typedef {Object} boxData
	 * @property {String} fgColor - (optional) the foreground color name (can be `white` or `almost-black`)
	 * @property {String} bgColor - background color name (refer to the @vg-faset/core color palette)
	 * @property {String|Object} bgPosition - (optional) enter 'right' or 'left', or define the bgPosition for each breakpoint in an object `{ sm: 'center bottom', md: 'left bottom', lg: 'left bottom' }`
	 * @property {String} graphic - graphic url
	 * @property {Object} bgSize - background size object for each breakpoint (sm/md/lg)
	 * @property {String} bgOverlayColor - (optional) color overlay (hexadecimal color value with hash, ex `#ffffff`). sits over the background image but behind the text content
	 * @property {String} bgOverlayOpacity - (optional) opacity value for the background color overlay (value range 0.0 to 1.0)
	 * @property {String} title - the <h3> title of the content box
	 * @property {String|Array} content - the <p> content of the content box. Pass an array to create multiple <p>
	 * @property {Object} contentMarginBottom - (optional) custom margin bottom for below the content paragraph element. Define the margin-bottom for each breakpoint in an object `{ sm: '80px', md: '80px', lg: '180px' }`
	 * @property {String|Object} maxWidth - (optional) set the max width of the content as a string `80%`, or as an object for each breakpoint `{ sm: '100%', md: '100%', lg: '80%' }`
	 * @property {String} ctaUrl - the href for the CTA button
	 * @property {String} ctaText - the text of the CTA button
	 * @property {String} ctaDescription - description of the CTA link functionality (for accessibility - read by screen readers)
	 * @property {Boolean} ctaLocked - whether to display the lock icon that denotes secure content
	 */
	props: ["boxData"],
	components: { VBreakpoint },
	data() {
		return {
			model: new Model(),
			fgColor: "white"
		};
	},
	mounted() {
		if (this.boxData.fgColor) {
			this.fgColor = this.boxData.fgColor;
		} else if (this.boxData.bgColor.indexOf("aeroblue") >= 0) {
			this.fgColor = "almost-black";
		}
	},
	methods: {
		onButtonClicked(e) {
			this.$emit("button-click", e);
		}
	},
	computed: {
		// content should be an array but the prop should be able to take an array
		// or just a string. So this will ensure that internally it's always an array
		content() {
			if (Array.isArray(this.boxData.content)) {
				return this.boxData.content;
			}

			return [this.boxData.content];
		},
		className() {
			let classes = [
				"pdp-content-box",
				`bg--${this.boxData.bgColor}`,
				`fg--${this.fgColor}`
			];

			if (this.boxData.bgPosition) {
				classes.push(this.boxData.bgPosition);
			}

			return classes.join(" ");
		},
		bgSize() {
			if (this.model.isSmall) {
				return this.boxData.bgSize.sm;
			} else if (this.model.isMedium) {
				return this.boxData.bgSize.md;
			} else if (this.model.isLarge) {
				return this.boxData.bgSize.lg;
			} else {
				return this.boxData.bgSize.lg;
			}
		},
		bgPosition() {
			// if the property exists, match with the screen size,
			// otherwise return null.
			if (this.boxData && this.boxData.bgPosition) {
				return this.model.isSmall
					? this.boxData.bgPosition.sm
					: this.model.isMedium
					? this.boxData.bgPosition.md
					: this.model.isLarge
					? this.boxData.bgPosition.lg
					: null;
			}

			return null;
		},
		contentStyles() {
			let marginBottom = "";
			let maxWidth = "";

			if (this.boxData) {
				if (this.boxData.contentMarginBottom) {
					marginBottom = this.model.isSmall
						? `margin-bottom: ${this.boxData.contentMarginBottom.sm};`
						: this.model.isMedium
						? `margin-bottom: ${this.boxData.contentMarginBottom.md};`
						: this.model.isLarge
						? `margin-bottom: ${this.boxData.contentMarginBottom.lg};`
						: null;
				}
				if (this.boxData.maxWidth) {
					if (typeof this.boxData.maxWidth === "string") {
						maxWidth = `max-width: ${this.boxData.maxWidth};`;
					} else if (typeof this.boxData.maxWidth === "object") {
						maxWidth = this.model.isSmall
							? `max-width: ${this.boxData.maxWidth.sm};`
							: this.model.isMedium
							? `max-width: ${this.boxData.maxWidth.md};`
							: this.model.isLarge
							? `max-width: ${this.boxData.maxWidth.lg};`
							: null;
					}
				}
			}

			return marginBottom + maxWidth;
		},
		bgOverlayOpacity() {
			return this.boxData.bgOverlayOpacity || 0;
		},
		bgColorOverlay() {
			return this.boxData.bgOverlayColor
				? `background-color: ${this.boxData.bgOverlayColor}; opacity: ${this.bgOverlayOpacity};`
				: null;
		}
	}
};
</script>

<style lang="scss">
.pdp-content-box {
	background-position: bottom right;
	background-repeat: no-repeat;
	margin-bottom: 40px;
	min-height: 360px;
	position: relative;

	// Override the small margins so the box goes edge-to-edge on these screens
	@include to-md {
		margin: 0 (-1 * $small-margin);
	}

	@include from-lg {
		background-size: auto;
	}

	&.left {
		background-position: -30px bottom;

		@include from-md {
			background-position: left bottom;
		}
	}

	.button {
		bottom: 40px;
		position: absolute;
		right: 30px;
	}

	&:last-child {
		margin-bottom: 0;
	}
}

.content-box__text {
	@include p2;

	// Better white-on-red font rendering
	-webkit-font-smoothing: antialiased;
	max-width: 440px;
	margin-bottom: 180px;

	@include from-md {
		&:last-of-type {
			margin-bottom: 140px;
		}
	}

	@include from-lg {
		&:last-of-type {
			margin-bottom: 180px;
		}
	}

	// margin between <p> content
	& + & {
		margin-top: 2em;
	}
}

.content-container {
	position: relative;
	width: 100%;
	height: 100%;
	padding: 40px 30px;
	z-index: 1;

	@include from-md {
		padding: 50px 40px;
	}
}

.background-overlay {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
}
</style>
